<script setup>
import dayjs from 'dayjs';
import HawkModalContainer from '~/common/components/hawk-modal/hawk-modal-container.vue';

const props = defineProps({
  isMenu: {
    type: Boolean,
    default: false,
  },
  asPopup: {
    type: Boolean,
    default: false,
  },
  seriesData: {
    type: Object,
    default: null,
  },
  submitLabel: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['save', 'close']);

const form_defaults = computed(() => {
  const generate_series = props.seriesData?.generateSeries;
  if (!generate_series)
    return {};
  const name = props.seriesData.name ?? props.seriesData.alias;
  if (generate_series.type === 'date') {
    const [interval, interval_unit] = String(generate_series.step ?? '1 day').split(' ');
    return {
      series_type: 'date',
      date_name: name,
      date_start: dayjs(generate_series.start).format('YYYY-M-D'),
      date_end: dayjs(generate_series.stop).format('YYYY-M-D'),
      interval: Number(interval),
      interval_unit: interval_unit ?? 'day',
    };
  }
  return {
    series_type: 'numeric',
    name,
    start: generate_series.start,
    end: generate_series.stop,
    interval: generate_series.step,
  };
});

function save(data, form$) {
  const form_data = form$.data;
  const type = form_data.series_type;
  let generateSeries;
  let alias;

  if (type === 'date') {
    const start_date = dayjs(form_data.date_start);
    const end_date = dayjs(form_data.date_end);
    generateSeries = {
      type: 'date',
      start: start_date.format('YYYY/MM/DD'),
      stop: end_date.format('YYYY/MM/DD'),
      step: `${form_data.interval} ${form_data.interval_unit}`,
      alias: 'date',
    };
    alias = form_data.date_name;
  }
  else if (type === 'numeric') {
    generateSeries = {
      type: 'numeric',
      start: form_data.start,
      stop: form_data.end,
      step: form_data.interval,
      alias: 'number',
    };
    alias = form_data.name;
  }
  emit('save', { generateSeries, name: alias, alias });
}

// Get start date of the current year, ex: 1-jan-2025
const startDate = new Date();
startDate.setFullYear(startDate.getFullYear());
startDate.setMonth(0);
startDate.setDate(1);

// Get end date of the current year, ex: 31-dec-2025
const endDate = new Date();
endDate.setFullYear(endDate.getFullYear());
endDate.setMonth(11);
endDate.setDate(31);

// Return the date in the format 2025-01-01
function getDate(date) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
</script>

<template>
  <component :is="asPopup ? HawkModalContainer : 'div'" :content_class="asPopup ? 'rounded-lg !w-[840px]' : undefined" @close="emit('close')">
    <HawkModalHeader v-if="asPopup" @close="emit('close')">
      <template #title>
        {{ title || $t('Generate series') }}
      </template>
    </HawkModalHeader>
    <div class="p-4">
      <Vueform size="sm" class="max-w-[800px]" :class="{ 'min-h-[80vh]': !isMenu && !asPopup }" :endpoint="save" :display-errors="false" :columns="{ sm: { container: 12, label: 4, wrapper: 12 } }">
        <div class="col-span-12 grid grid-cols-12 gap-4">
          <RadiogroupElement
            name="series_type" label="Series type" :default="form_defaults.series_type || 'date'" :rules="['required']"
            :items="[{ label: 'Date', value: 'date' }, { label: 'Number', value: 'numeric' }]"
          />
          <TextElement name="date_name" label="Series name" :conditions="[['series_type', '==', 'date']]" class="w-full" :rules="['required']" :default="form_defaults.date_name || 'Timeseries'" />
          <TextElement name="name" label="Series name" :conditions="[['series_type', '==', 'numeric']]" class="w-full" :rules="['required']" :default="form_defaults.name || 'Number series'" />

          <GroupElement name="date_range" label="Date range" :conditions="[['series_type', '==', 'date']]" :rules="['required']">
            <DateTimeElement
              name="date_start"
              :rules="['required']"
              :default="form_defaults.date_start || getDate(startDate)"
              :options="{
                teleport: false,
              }"
              :columns="{
                default: 12,
                sm: isMenu ? 12 : 6,
              }"
            />
            <DateTimeElement
              name="date_end"
              :rules="['required', 'after:date_range.date_start']"
              :default="form_defaults.date_end || getDate(endDate)"
              :options="{
                teleport: false,
              }"
              :columns="{
                default: isMenu ? 12 : 6,
                sm: isMenu ? 12 : 6,
              }"
            />
          </GroupElement>
          <GroupElement name="range" label="Range" :conditions="[['series_type', '==', 'numeric']]" :rules="['required']">
            <TextElement
              name="start"
              input-type="number"
              :rules="['required']"
              :default="form_defaults.start ?? 1"
              :columns="{
                default: 12,
                sm: 6,
              }"
            />
            <TextElement
              name="end"
              input-type="number"
              :rules="['required', 'gt:range.start']"
              :default="form_defaults.end ?? 100"
              :columns="{
                default: 6,
                sm: 6,
              }"
            />
          </GroupElement>
          <GroupElement :conditions="[['series_type', '==', 'date']]" name="interval_group" label="Interval" :rules="['required']">
            <TextElement
              name="interval"
              input-type="number"
              :rules="['required']"
              :default="form_defaults.interval ?? 1"
              :columns="{
                default: 12,
                sm: 8,
              }"
            />
            <SelectElement
              name="interval_unit" :items="[{ label: 'day', value: 'day' }, { label: 'week', value: 'week' }, { label: 'month', value: 'month' }]"
              :default="form_defaults.interval_unit || 'day'"
              :rules="['required']"
              :columns="{
                default: 12,
                sm: 4,
              }"
            />
          </GroupElement>
          <TextElement
            name="interval"
            label="Interval"
            input-type="number"
            :default="form_defaults.interval ?? 1"
            :rules="['required']"
            :conditions="[['series_type', '==', 'numeric']]"
          />
          <div class="col-span-12 flex justify-end">
            <ButtonElement :submits="true" name="submit">
              {{ submitLabel || $t('Next') }}
            </ButtonElement>
          </div>
        </div>
      </Vueform>
    </div>
  </component>
</template>
